/**
 * useApiFileSrc (KAN-618, generalized in KAN-770)
 *
 * Source for <img src> of a file from the authenticated API. A path to the API
 * (/api/...) is loaded through the api client (token) into an object URL; any
 * other URL (S3 presigned) is returned as is. null: no file (yet) or it failed
 * to load. Caching and revoking: lib/apiFileCache.
 */
import { useEffect, useState } from 'react';
import { fetchApiFile, isApiFileUrl } from '@/services/api/apiFiles';
import { loadApiFileUrl, type ApiFileFetcher } from '@/lib/apiFileCache';

export type { ApiFileFetcher };

/**
 * @param url file url from the backend ("" when there is no file)
 * @param version changes when the file changes; "" when unknown, then the
 *   cached file is used
 * @param fetcher loads the blob (default: api client with the token)
 */
export function useApiFileSrc(
  url: string,
  version = '',
  fetcher: ApiFileFetcher = fetchApiFile
): string | null {
  const viaApi = isApiFileUrl(url);
  const key = `${version}|${url}`;
  const [loaded, setLoaded] = useState<{ key: string; src: string } | null>(
    null
  );

  useEffect(() => {
    if (!viaApi) return undefined;
    let cancelled = false;
    loadApiFileUrl(url, version, fetcher)
      .then((src) => {
        if (!cancelled) setLoaded({ key, src });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [url, viaApi, version, key, fetcher]);

  if (!url) return null;
  if (!viaApi) return url;
  return loaded?.key === key ? loaded.src : null;
}
